import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useWallet } from '../../contexts/WalletContext'
import { getAffiliateMe } from '../../services/affiliateService'
import { apiRequest } from '../../services/apiRequest'
import type { AffiliateMeData } from '../../services/affiliateService'
import {
  affiliateWithdrawProgress,
  affiliateWithdrawShortfall,
  canAffiliateCommissionWithdraw,
  resolveMinCommissionWithdraw,
} from './affiliateConstants'

const AffiliateWithdrawalPage: React.FC = () => {
  const navigate = useNavigate()
  const { formatCurrency } = useWallet()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [affiliateData, setAffiliateData] = useState<AffiliateMeData | null>(null)
  const [amount, setAmount] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const me = await getAffiliateMe()
        if (!cancelled) setAffiliateData(me)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const remaining = affiliateData?.remainingEarnings ?? 0
  const minCommissionWithdraw = resolveMinCommissionWithdraw(
    affiliateData?.minCommissionWithdrawalAmount,
  )
  const canWithdraw = canAffiliateCommissionWithdraw(remaining, minCommissionWithdraw)
  const shortfall = affiliateWithdrawShortfall(remaining, minCommissionWithdraw)
  const progressPct = affiliateWithdrawProgress(remaining, minCommissionWithdraw)

  const payoutMode = useMemo(() => {
    const userMode = affiliateData?.userPayoutMode
    if (userMode && userMode !== 'inherit') return userMode
    return affiliateData?.payoutMode || 'manual'
  }, [affiliateData?.payoutMode, affiliateData?.userPayoutMode])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const num = parseFloat(amount.replace(/,/g, ''))
    if (isNaN(num) || num < minCommissionWithdraw) {
      Swal.fire({
        icon: 'warning',
        title: 'จำนวนไม่ถูกต้อง',
        text: `กรุณาระบุจำนวนขั้นต่ำ ${minCommissionWithdraw} บาท`
      })
      return
    }
    if (num > remaining) {
      Swal.fire({
        icon: 'warning',
        title: 'ยอดไม่เพียงพอ',
        text: `รายได้คงเหลือ ${formatCurrency(remaining)}`
      })
      return
    }
    setSubmitting(true)
    try {
      const res = await apiRequest<{
        success: boolean
        pendingApproval?: boolean
        message?: string
        data?: { paidAmount?: number; lockedAmount?: number }
      }>(
        '/api/v1/user-auth/affiliate/withdraw-request',
        { method: 'POST', body: { amount: num } }
      )
      if (res.success && res.pendingApproval) {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: res.message || 'รออนุมัติ'
        }).then(() => {
          setAmount('')
          navigate('/affiliate')
        })
      } else if (res.success) {
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: res.message || 'ค่าคอมมิชชั่นเข้าเครดิตแล้ว'
        }).then(() => {
          setAmount('')
          navigate('/affiliate')
        })
      } else {
        Swal.fire({
          icon: 'info',
          title: 'แจ้งเตือน',
          text: (res as { message?: string }).message || 'ไม่สามารถดำเนินการได้ในขณะนี้'
        })
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'เกิดข้อผิดพลาด'
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: msg
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleWithdrawAll = () => {
    setAmount(String(Math.floor(remaining)))
  }

  if (loading) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 col-lg-8 offset-lg-2 px-1">
              <div className="text-center py-3">
                <div className="spinner-border text-primary" role="status" />
                <p className="mt-2">กำลังโหลดข้อมูล...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="content pb-3">
        <div className="container">
          <div className="row px--1">
            <div className="col-12 col-lg-8 offset-lg-2 px-1">
              <div className="text-center py-3">
                <p className="c-red mb-2">{error}</p>
                <button type="button" className="check-button bgg-4" onClick={() => navigate('/affiliate')}>
                  กลับไปแนะนำเพื่อน
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 col-lg-8 offset-lg-2 px-1">
            <div className="text-center">
              <div className="bdr-5 bx-sd mb-2">
                <div className="w-100 p-2 bgc-4 wrap justify-content-between mb-2">
                  <a href="#" className="txt-link-d" onClick={(e) => { e.preventDefault(); navigate('/affiliate') }}>
                    <i className="fas fa-chevron-left me-1"></i>
                    กลับไปแนะนำเพื่อน
                  </a>
                </div>
              </div>

              <div className="row mx--1 mb-2">
                <div className="col-12 px-1">
                  <div className="bgg-1 bdr-5 bdc-1 p-3 text-center">
                    <div className="bgc-4 px-1 py-1 t-regular bdr-5 mb-2">
                      <small>รายได้คงเหลือ (บาท)</small>
                    </div>
                    <div className="px-1 t-regular c-white">
                      <h3 className="mb-0">{formatCurrency(remaining)}</h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bdr-5 bgc-4 p-3 mb-2 text-start">
                <div className="fs-18 t-medium c-red mb-2">
                  <i className="fas fa-dollar-sign me-2"></i>
                  ถอนคอมมิชชั่น
                </div>

                <div className={`p-3 bdr-10 mb-3 ${canWithdraw ? 'bgc-9' : 'bgc-7'}`}>
                  <div className="t-medium c-white mb-1">
                    {canWithdraw ? 'พร้อมส่งคำขอถอน' : 'ยังถอนไม่ได้ — ยอดยังไม่ถึงขั้นต่ำ'}
                  </div>
                  <div className="fs-13 c-white" style={{ opacity: 0.85 }}>
                    {canWithdraw
                      ? `ถอนได้ขั้นต่ำ ${minCommissionWithdraw} บาท และไม่เกินยอดคงเหลือ`
                      : `ต้องมีรายได้คงเหลืออย่างน้อย ${minCommissionWithdraw} บาท (ขาดอีก ${formatCurrency(shortfall)} บาท)`}
                  </div>
                  <div className="d-flex justify-content-between fs-12 c-white mt-3 mb-1" style={{ opacity: 0.8 }}>
                    <span>ความคืบหน้าสู่ขั้นต่ำ {minCommissionWithdraw} บาท</span>
                    <span>{progressPct.toFixed(0)}%</span>
                  </div>
                  <div className="bdr-10 overflow-hidden" style={{ height: 10, background: 'rgba(0,0,0,0.2)' }}>
                    <div
                      style={{
                        width: `${progressPct}%`,
                        height: '100%',
                        background: canWithdraw ? '#31d07f' : '#fbbf24',
                        transition: 'width 0.35s ease',
                      }}
                    />
                  </div>
                </div>

                {!canWithdraw ? (
                  <div className="bgc-7 bdr-10 p-3 mb-2 text-start">
                    <div className="t-medium c-white mb-2">
                      <i className="fas fa-lightbulb me-2"></i>
                      วิธีเพิ่มรายได้คอมมิชชั่น
                    </div>
                    <ul className="fs-13 c-white mb-0 ps-3" style={{ opacity: 0.9 }}>
                      <li className="mb-1">แชร์ลิงก์แนะนำเพื่อนจากหน้าภาพรวม</li>
                      <li className="mb-1">รับคอมมิชชั่นเมื่อสมาชิกที่แนะนำเล่นและรอบ/เกมสรุปผลแล้ว</li>
                      <li>เมื่อยอดถึง {minCommissionWithdraw} บาท ปุ่มส่งคำขอถอนจะเปิดให้ใช้งานทันที</li>
                    </ul>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-2">
                      <label className="form-label text-start w-100 c-dark">จำนวนเงิน (บาท)</label>
                      <div className="d-flex flex-column flex-sm-row gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-control bgc-7 bdr-5 flex-grow-1"
                          placeholder={`ขั้นต่ำ ${minCommissionWithdraw} บาท`}
                          value={amount}
                          onChange={(e) => setAmount(e.target.value.replace(/\D/g, ''))}
                        />
                        <button type="button" className="check-button bgg-1 bdc-1 c-white" onClick={handleWithdrawAll}>
                          ถอนทั้งหมด
                        </button>
                      </div>
                    </div>
                    <p className="fs-12 text-muted text-start">
                      {payoutMode === 'auto'
                        ? 'เมื่อส่งคำขอ ระบบจะโอนค่าคอมมิชชั่นเข้าเครดิตทันที'
                        : 'เมื่อส่งคำขอ ระบบจะล็อกยอดและรอแอดมินอนุมัติ'}
                    </p>
                    <button
                      type="submit"
                      className="check-button bgg-4 c-white w-100"
                      disabled={submitting || !amount}
                    >
                      {submitting ? 'กำลังส่งคำขอ...' : 'ส่งคำขอถอน'}
                    </button>
                  </form>
                )}
              </div>

              <button
                type="button"
                className="check-button bgg-1 bdc-1 c-white w-100"
                onClick={() => navigate('/affiliate')}
              >
                กลับไปแนะนำเพื่อน
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AffiliateWithdrawalPage
